import React from 'react';
import classNames from 'classnames';
import css from './TopbarSearchForm.module.css';

const formatMoney = (amount, currency) => {
  if (amount == null || !Number.isFinite(amount)) {
    return null;
  }
  try {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currency || 'EUR' }).format(
      amount / 100
    );
  } catch (e) {
    return `${(amount / 100).toFixed(2)} ${currency || 'EUR'}`;
  }
};

const resolveItemDescription = item => {
  return (
    item?.shortDescription ||
    item?.publicData?.short_description ||
    item?.publicData?.shortdescription ||
    item?.publicData?.shortDescription ||
    ''
  );
};

const LiveSearchMegaMenu = props => {
  const {
    isVisible,
    suggestions,
    activeIndex,
    isLoading,
    isMobile,
    hasQuery,
    recentlyViewedItems = [],
    onRequestClose,
    onItemHover,
    onItemSelect,
  } = props;

  if (!isVisible) {
    return null;
  }

  const renderRows = ({ items, activeOffset = 0, trackActive = true }) => (
    <ul className={css.liveSearchList}>
      {items.map((item, index) => {
        const absoluteIndex = activeOffset + index;
        const isActive = trackActive && activeIndex === absoluteIndex;
        const isBrand = item.type === 'brand';
        const rowKey = isBrand
          ? `brand_${item.slug || item.productTitle}_${index}`
          : `${item.listingId}_${index}`;
        return (
          <li
            key={rowKey}
            className={isActive ? css.liveSearchItemActive : css.liveSearchItem}
          >
            <button
              type="button"
              className={css.liveSearchButton}
              role="option"
              aria-selected={isActive}
              onMouseEnter={() => (trackActive ? onItemHover(absoluteIndex) : null)}
              onMouseDown={event => event.preventDefault()}
              onClick={() => onItemSelect(item)}
            >
              {item.imageUrl ? (
                <img
                  src={item.imageUrl}
                  alt=""
                  loading="lazy"
                  className={classNames(css.liveSearchImage, {
                    [css.liveSearchImageBrand]: isBrand,
                    [css.liveSearchImageBrandDark]: isBrand && item.logoOnDark,
                  })}
                  width="48"
                  height="48"
                />
              ) : (
                <div className={css.liveSearchImagePlaceholder} />
              )}
              <div className={css.liveSearchContent}>
                <div className={css.liveSearchHeaderRow}>
                  <span className={css.liveSearchBrand}>{item.brand}</span>
                  {!isBrand && (item.priceAmount != null || item.originalPriceAmount != null) && (
                    <div className={css.liveSearchPriceRow}>
                      {item.originalPriceAmount != null &&
                      item.priceAmount != null &&
                      item.originalPriceAmount > item.priceAmount ? (
                        <span className={css.liveSearchDiscountPill}>
                          -{formatMoney(item.originalPriceAmount - item.priceAmount, item.priceCurrency)}
                        </span>
                      ) : null}
                      {item.originalPriceAmount != null &&
                      item.priceAmount != null &&
                      item.originalPriceAmount > item.priceAmount ? (
                        <span className={css.liveSearchOriginalPrice}>
                          {formatMoney(item.originalPriceAmount, item.priceCurrency)}
                        </span>
                      ) : null}
                      {item.priceAmount != null ? (
                        <span className={css.liveSearchPrice}>
                          {formatMoney(item.priceAmount, item.priceCurrency)}
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>
                <span className={css.liveSearchTitle}>{item.productTitle}</span>
                <span className={css.liveSearchDescription}>{resolveItemDescription(item)}</span>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );

  const hasRecentItems = !hasQuery && recentlyViewedItems.length > 0;

  return (
    <div
      className={classNames(css.liveSearchMegaMenu, { [css.liveSearchMegaMenuMobile]: isMobile })}
      role="listbox"
      aria-label="Suggestions de recherche"
    >
      <button
        type="button"
        className={css.liveSearchCloseButton}
        aria-label="Fermer les suggestions"
        onClick={onRequestClose}
      >
        ×
      </button>

      <div className={css.liveSearchScrollArea}>
        {isLoading ? <div className={css.liveSearchStatus}>Recherche en cours...</div> : null}

        {!isLoading && !hasQuery && !hasRecentItems ? (
          <div className={css.liveSearchStatus}>Commencez à taper pour voir les suggestions</div>
        ) : null}

        {!isLoading && hasRecentItems ? (
          <div className={css.liveSearchRecentSection}>
            <div className={css.liveSearchSectionTitle}>Vos derniers produits consultés</div>
            {renderRows({ items: recentlyViewedItems, trackActive: false })}
          </div>
        ) : null}

        {!isLoading && hasQuery && suggestions.length === 0 ? (
          <div className={css.liveSearchStatus}>Aucun resultat</div>
        ) : null}

        {!isLoading && suggestions.length > 0 ? renderRows({ items: suggestions }) : null}
      </div>
    </div>
  );
};

export default LiveSearchMegaMenu;
